import { useState, useEffect, useCallback } from 'react';
import { useInView } from '../hooks/useInView';
import { testimonials } from '../data/mock';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';

export const TestimonialsSection = () => {
  const [ref, isInView] = useInView();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth < 768) setItemsPerView(1);
      else if (window.innerWidth < 1024) setItemsPerView(2);
      else setItemsPerView(3);
    };
    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - itemsPerView);

  const scrollPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const scrollNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  }, [maxIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [maxIndex]);

  return (
    <section id="testimonials" className="relative py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <div
          className={`text-center mb-6 transition-[opacity,transform] duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Google Reviews badge */}
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 mb-6 shadow-sm">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 text-[#FBBC05] fill-[#FBBC05]" />)}
            </div>
            <span className="text-sm text-slate-600 font-medium">Google Reviews</span>
          </div>

          <h2
            className="text-4xl sm:text-5xl font-bold text-[#0A1628] mt-3 mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            What Our Students Say
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Real reviews from real students on Google. See why 3000+ students trust Chris IELTS Consulting.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div
          className={`transition-[opacity,transform] duration-700 delay-300 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="flex-shrink-0 px-3" style={{ width: `${100 / itemsPerView}%` }}>
                  <div className="bg-white rounded-2xl p-8 border border-slate-100 h-full flex flex-col hover:shadow-lg hover:shadow-[#0A1628]/5 transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
                    <Quote className="w-10 h-10 text-[#D4A853]/20 mb-4" />

                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex gap-0.5">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-[#FBBC05] fill-[#FBBC05]" />
                        ))}
                      </div>
                      {testimonial.isLocalGuide && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Local Guide</span>
                      )}
                    </div>

                    <p className="text-slate-600 leading-relaxed flex-1 mb-6 text-[15px]">
                      "{testimonial.review}"
                    </p>

                    <div className="flex items-center gap-3 pt-5 border-t border-slate-100">
                      <Avatar className="w-11 h-11">
                        <AvatarFallback className="bg-[#0A1628] text-[#E8C87A] font-semibold text-sm">
                          {testimonial.avatar}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-[#0A1628] text-sm">{testimonial.name}</p>
                        <p className="text-xs text-slate-400">{testimonial.date} · {testimonial.source}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center mt-10 gap-4 items-center">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full border-slate-200 text-[#0A1628] hover:bg-[#0A1628] hover:text-white hover:border-[#0A1628] transition-colors duration-300 disabled:opacity-30"
              onClick={scrollPrev}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-[background-color,transform] duration-300 ${
                    i === currentIndex ? 'bg-[#D4A853] scale-125' : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full border-slate-200 text-[#0A1628] hover:bg-[#0A1628] hover:text-white hover:border-[#0A1628] transition-colors duration-300 disabled:opacity-30"
              onClick={scrollNext}
              disabled={currentIndex >= maxIndex}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
